// Frontend wrappers for service-related Cloud Functions and Firestore
// reads. Components import from here rather than reaching into the SDK
// so the call surface stays auditable and easy to mock.

import { httpsCallable } from 'firebase/functions';
import { getDoc } from 'firebase/firestore';
import { functions } from '../firebase';
import { orgServiceDoc } from './firestore';
import type { Service, ServiceStatus, ServiceTier } from './types';

interface CreateServiceRequest {
  orgId: string;
  name: string;
  tier?: ServiceTier;
  description?: string;
}

interface CreateServiceResponse {
  serviceId: string;
}

export async function createService(
  orgId: string,
  input: { name: string; tier?: ServiceTier; description?: string },
): Promise<string> {
  const callable = httpsCallable<CreateServiceRequest, CreateServiceResponse>(
    functions,
    'createService',
  );
  const result = await callable({ orgId, ...input });
  return result.data.serviceId;
}

export async function updateService(
  orgId: string,
  serviceId: string,
  updates: {
    name?: string;
    tier?: ServiceTier;
    description?: string;
    publiclyVisible?: boolean;
    escalationPolicyId?: string | null;
  },
): Promise<void> {
  const callable = httpsCallable(functions, 'updateService');
  await callable({ orgId, serviceId, ...updates });
}

export async function deleteService(orgId: string, serviceId: string): Promise<void> {
  const callable = httpsCallable(functions, 'deleteService');
  await callable({ orgId, serviceId });
}

export async function getService(orgId: string, serviceId: string): Promise<Service | null> {
  const snap = await getDoc(orgServiceDoc(orgId, serviceId));
  if (!snap.exists()) return null;
  return snap.data() as Service;
}

// Display helpers — small lookup tables so we can theme tier and status
// badges consistently across the app.

export const TIER_LABELS: Record<ServiceTier, string> = {
  tier1: 'Tier 1',
  tier2: 'Tier 2',
  tier3: 'Tier 3',
};

export const TIER_TONES: Record<ServiceTier, 'danger' | 'warning' | 'neutral'> = {
  tier1: 'danger',
  tier2: 'warning',
  tier3: 'neutral',
};

export const STATUS_LABELS: Record<ServiceStatus, string> = {
  operational: 'Operational',
  degraded: 'Degraded',
  partial_outage: 'Partial outage',
  major_outage: 'Major outage',
  maintenance: 'Maintenance',
};

export const STATUS_TONES: Record<ServiceStatus, 'success' | 'warning' | 'danger' | 'info'> = {
  operational: 'success',
  degraded: 'warning',
  partial_outage: 'warning',
  major_outage: 'danger',
  maintenance: 'info',
};
